import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Phone, MapPin } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SiteHeader } from '../components/home/SiteHeader';
import { SiteFooter } from '../components/home/SiteFooter';
import { FinancingCTA } from '../components/FinancingCTA';
import { Breadcrumb } from '../components/Breadcrumb';
import { CrossSellCTA, EstimateCTA } from '../components/CrossSellCTA';
import { trackClick } from '../utils/analytics';

const RanchHorseFence = () => {
  useEffect(() => { document.title = 'Ranch & Horse Fence | North Texas Welding & Fabrication'; }, []);
  return (
  <div className="min-h-screen bg-white" data-testid="ranch-horse-fence-page">
    <Helmet>
      <meta name="description" content="Complete ranch and horse fencing solutions across North Texas. Pipe fence, cross-fencing, paddocks, pastures, and perimeter fencing. Springtown, Weatherford, Azle." />
      <link rel="canonical" href="https://ntxpipefence.com/ranch-horse-fence" />
      <meta property="og:title" content="Ranch & Horse Fence | North Texas Welding & Fabrication" />
      <meta property="og:description" content="Complete ranch and horse fencing solutions across North Texas." />
    </Helmet>

    <SiteHeader mobileMenuOpen={false} setMobileMenuOpen={() => {}} />

    <div className="pt-24">
      <Breadcrumb items={[{ label: 'Pipe Fencing', href: '/pipe-fencing' }, { label: 'Ranch & Horse Fence' }]} />
    </div>

    {/* Hero */}
    <section className="bg-zinc-900 py-16 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzI2MjYyNiIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Ranch & Horse Fencing</h1>
            <p className="text-zinc-400 text-lg mb-3">Complete Fencing Solutions for Working Properties</p>
            <p className="text-zinc-300 leading-relaxed mb-8">
              Ranch and horse properties across North Texas depend on fencing that works as hard as the people who own them. North Texas Welding and Fabrication builds complete ranch fencing systems — from perimeter pipe fencing that defines your property line to cross-fencing that divides pastures, paddocks that safely contain horses, and working pens that handle cattle. We understand ranch fencing because we serve ranch owners every day.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/instant-estimate" onClick={() => trackClick('Estimate - Ranch', '/ranch-horse-fence')} className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-lg px-8 py-4 rounded-lg transition-colors">
                Get an Instant Estimate <ArrowRight size={18} />
              </Link>
              <a href="tel:8177235006" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-black font-semibold text-lg px-8 py-4 rounded-lg transition-colors">
                <Phone size={18} /> 817-723-5006
              </a>
            </div>
          </div>
          <div>
            <img src="/images/farm-ranch-perimeter.jpg" alt="Ranch Pipe Fence Installation" className="rounded-xl shadow-2xl w-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </div>
    </section>

    {/* Content */}
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-zinc-900 mb-6">Fencing Built for Working Ranch Properties</h2>
        <p className="text-zinc-700 leading-relaxed mb-4">
          A working ranch is only as good as its fencing. Whether you run cattle, raise horses, operate a breeding facility, or simply manage rural acreage, your fence system needs to contain animals reliably, define property boundaries clearly, and stand up to years of daily use without constant repair.
        </p>
        <p className="text-zinc-700 leading-relaxed mb-4">
          Our ranch fencing is available in both drill pipe and new pipe options. Drill pipe is heavy-gauge steel proven in demanding oil field applications, and new pipe offers brand-new material with a clean, consistent finish. Posts are set 3-4 feet deep in concrete, corner and gate posts go deeper with additional bracing, and all connections are precision welded for a clean, strong joint. We combine pipe construction with 12.5-gauge no-climb wire for livestock containment, or build open-rail configurations (3-rail, 4-rail, 5-rail) for horse pastures and property aesthetics.
        </p>
        <p className="text-zinc-700 leading-relaxed mb-8">
          What sets our ranch fencing apart is scale and capability. We fence properties from 5 acres to 500+ acres, we build working pens and sorting facilities, and we fabricate custom gates that match your fence system. Our land services crew can clear overgrown fence lines before installation, making us a single-source solution for the entire project.
        </p>

        <h2 className="text-3xl font-bold text-zinc-900 mb-6">Ranch Fencing Applications</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {[
            'Property perimeter fencing — define and secure boundaries',
            'Cross-fencing and pasture division for rotational grazing',
            'Horse paddocks and turnout areas with no-climb wire',
            'Cattle working pens, alleys, and sorting facilities',
            'Hay lot and equipment yard fencing',
            'Arena and round pen construction',
            'Breeding facility fencing with specialized containment',
            'Entry gates and ranch entrance systems',
            'Wildlife management fencing and high-fence options',
            'Fence-line clearing and old fence removal'
          ].map(point => (
            <div key={point} className="flex items-start gap-3 p-3 bg-zinc-50 rounded-lg">
              <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <Check className="text-white" size={12} />
              </div>
              <p className="text-zinc-700 text-sm">{point}</p>
            </div>
          ))}
        </div>

        <h2 className="text-3xl font-bold text-zinc-900 mb-6">Horse Property Considerations</h2>
        <p className="text-zinc-700 leading-relaxed mb-4">
          Horses require fencing that prioritizes safety above all else. Our horse fencing solutions use smooth pipe rails and no-climb wire to eliminate the injury risks common with barbed wire, T-posts, and board fencing. We design paddock layouts that work for your operation — accounting for barn access, water points, run-in shed placement, and daily horse traffic patterns.
        </p>
        <p className="text-zinc-700 leading-relaxed mb-4">
          For equestrian facilities in the Weatherford, Springtown, and Azle areas, we build arenas, round pens, and paddock systems that meet the standards expected by serious horse people. Our pipe fence construction eliminates the maintenance headaches of wood — no painting, no board replacement, no rotting posts — while providing a cleaner, more professional appearance.
        </p>
      </div>
    </section>

    {/* Materials (merged from /services/ranch-fence) */}
    <section className="py-16 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-zinc-900 mb-3 text-center">Ranch Fence Materials</h2>
        <p className="text-zinc-600 text-center mb-10 max-w-2xl mx-auto">Quality materials built for demanding ranch conditions.</p>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { name: 'Heavy-Duty Pipe Rails', desc: 'Drill pipe or new pipe for top and mid rails — your choice of material. Both provide excellent strength and livestock visibility.' },
            { name: 'Reinforced Corner Posts', desc: 'Extra-heavy corner and brace posts set deeper with additional concrete for maximum stability.' },
            { name: 'High-Tensile Wire Options', desc: 'Can incorporate wire mesh or strands for additional livestock control while maintaining pipe strength.' },
            { name: 'Quality Hardware', desc: 'Galvanized clips, bolts, and connections designed for Texas weather conditions.' }
          ].map(m => (
            <div key={m.name} className="bg-white rounded-lg p-6 border border-zinc-200">
              <h3 className="text-lg font-bold text-zinc-900 mb-2">{m.name}</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Pricing Factors (merged from /services/ranch-fence) */}
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-zinc-900 mb-8 text-center">Ranch Fence Installation Costs</h2>
        <div className="bg-zinc-50 rounded-xl p-8 border border-zinc-200">
          <ul className="space-y-3 mb-6">
            {[
              'Total linear footage (most ranches: 1000-5000+ feet)',
              'Terrain challenges (flat vs hilly, rocky soil)',
              'Number of corners and turns',
              'Gate locations and quantities',
              'Height requirements (4ft, 5ft, or 6ft standard)',
              'Wire addition for livestock control',
              'Property clearing and preparation needs'
            ].map(f => (
              <li key={f} className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-2 shrink-0"></div>
                <span className="text-zinc-700">{f}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-zinc-500 border-t border-zinc-200 pt-4 italic">Average cost: $15-35 per linear foot installed. A square 10-acre property (about 2,640 feet of fence) typically costs $39,600-92,400 installed. Larger projects often qualify for volume pricing.</p>
        </div>
      </div>
    </section>

    <EstimateCTA heading="Get a Ranch Fencing Estimate" subtext="See preliminary pricing for your ranch or horse property fencing project." pagePath="/ranch-horse-fence" />

    {/* FAQs */}
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-zinc-900 mb-8 text-center">Ranch Fencing FAQ</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { q: 'How much does ranch fencing cost per acre?', a: 'Ranch fencing is typically priced per linear foot rather than per acre, since the perimeter footage varies based on property shape. A square 10-acre property has roughly 2,640 linear feet of perimeter (about $39,600-92,400 installed at $15-35/ft). Use our Instant Estimate tool for a preliminary range, or call for a free site consultation.' },
            { q: 'What is the best fence for horses in North Texas?', a: 'Pipe fence with no-climb wire is widely considered the safest and most durable option for horses in North Texas. The smooth pipe rails and tight wire mesh eliminate injury risks, the steel construction withstands horse pressure, and the minimal maintenance requirements keep long-term costs low. We install this combination across the region.' },
            { q: 'What height fence do I need for cattle vs horses?', a: 'For cattle, we generally recommend 4-5 foot tall fence (5-6 feet for bulls and breeding operations). For horses, 5 feet is standard. Specific recommendations vary by breed and operation — we discuss this during site consultation.' },
            { q: 'Can you fence a large ranch (100+ acres)?', a: 'Yes. We have fenced properties from 10 to 500+ acres. Our mobile welding rigs work efficiently across large properties, and we coordinate materials, crew scheduling, and access logistics for multi-week projects. Typical installation speed is 500-800 linear feet per week.' },
            { q: 'Do you build cattle working pens and sorting facilities?', a: 'Yes. We design and build working pens, sorting alleys, chutes, and holding areas specifically for cattle operations. Our welding capability means every pen is built with solid, welded connections rather than bolted joints that loosen over time.' },
            { q: 'Can you remove my old fence before installing new?', a: 'Yes. We offer old fence removal and disposal as part of our service. Whether you are replacing old wood, wire, or damaged pipe fencing, we handle the teardown and properly dispose of the materials before starting the new installation.' }
          ].map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border border-zinc-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-zinc-900 hover:text-red-600 py-4">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed pb-4">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>

    <CrossSellCTA
      items={[
        { eyebrow: 'Need land cleared first?', label: 'Land Services', href: '/land-services', desc: 'Clear brush, trees, and overgrown fence lines before your new fence goes in.' },
        { eyebrow: 'Adding a gate?', label: 'Custom Gates', href: '/custom-gates', desc: 'Custom ranch gates with your brand or logo — automated options available.' },
        { eyebrow: 'Building on the property?', label: 'Metal Buildings', href: '/metal-buildings', desc: 'Shops, barns, and agricultural buildings for your ranch property.' },
        { eyebrow: 'Need concrete?', label: 'Concrete Services', href: '/concrete', desc: 'Barn slabs, driveways, wash racks, and equipment pads.' }
      ]}
      title="Complete Your Ranch Project"
      subtitle="One contractor for fencing, land clearing, buildings, concrete, and gates."
      pagePath="/ranch-horse-fence"
    />

    <section className="py-12 bg-zinc-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-bold text-zinc-900 mb-6">Ranch Fencing Across North Texas</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {['Springtown', 'Weatherford', 'Azle', 'Decatur', 'Bridgeport', 'Aledo', 'Fort Worth', 'Mineral Wells', 'Boyd', 'Poolville'].map(city => (
            <span key={city} className="bg-white border border-zinc-200 rounded-lg px-4 py-2 text-sm font-medium text-zinc-700">
              <MapPin size={12} className="inline mr-1 text-red-600" />{city}, TX
            </span>
          ))}
        </div>
      </div>
    </section>

    <FinancingCTA sourcePage="/ranch-horse-fence" />
    <SiteFooter />
  </div>
);};

export default RanchHorseFence;
